import * as React from 'react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Spinner } from '@/components/ui/spinner'

/**
 * Dense, modern data table built on TanStack Table.
 *
 * Features: per-column sorting, drag-to-resize, drag-to-reorder, and
 * persistence of column widths + order to localStorage (keyed by `tableId`).
 *
 * Density is the point — rows are compact so ~20 fit without scrolling.
 *
 * Props:
 *  - columns      TanStack column defs. Use string `header` for the label.
 *                 `meta.align` ('left'|'right'|'center'), `meta.headerTitle`,
 *                 `meta.cellClassName` are honored.
 *  - data         row array
 *  - tableId      localStorage key for persisted sizing/order (omit to disable)
 *  - sorting/onSortingChange/manualSorting  controlled sorting (e.g. Redux)
 *  - fitMode      'fill' (normalize widths to 100%, no h-scroll) |
 *                 'fixed' (px widths, horizontal scroll when needed)
 *  - minWidth     min table width in px for 'fixed' mode
 *  - reorderable  enable header drag-reorder (default true)
 *  - isLoading / emptyMessage / getRowId
 */
function DataTable({
  columns,
  data,
  tableId,
  sorting: controlledSorting,
  onSortingChange,
  manualSorting = false,
  enableSortingRemoval = true,
  fitMode = 'fill',
  minWidth = 0,
  reorderable = true,
  isLoading = false,
  emptyMessage = 'No results.',
  getRowId,
  className,
}) {
  const storageKey = tableId ? `dt:${tableId}` : null

  const [internalSorting, setInternalSorting] = React.useState([])
  const [columnSizing, setColumnSizing] = React.useState(() => readStore(storageKey, 'sizing', {}))
  const [columnOrder, setColumnOrder] = React.useState(() => readStore(storageKey, 'order', []))
  const [dragOver, setDragOver] = React.useState(null)
  const dragSrc = React.useRef(null)
  // Set true while a pointer press starts on an in-header control (the sort
  // button) so a stray pointer move can't initiate a column reorder drag.
  const suppressReorderRef = React.useRef(false)

  React.useEffect(() => {
    const clear = () => { suppressReorderRef.current = false }
    window.addEventListener('pointerup', clear)
    return () => window.removeEventListener('pointerup', clear)
  }, [])

  const sorting = controlledSorting ?? internalSorting

  // Persist sizing + order
  React.useEffect(() => {
    if (storageKey) writeStore(storageKey, 'sizing', columnSizing)
  }, [storageKey, columnSizing])
  React.useEffect(() => {
    if (storageKey && columnOrder.length) writeStore(storageKey, 'order', columnOrder)
  }, [storageKey, columnOrder])

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnSizing, columnOrder },
    getRowId,
    onSortingChange: onSortingChange ?? setInternalSorting,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    manualSorting,
    enableSortingRemoval,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    getCoreRowModel: getCoreRowModel(),
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
  })

  const leafColumns = table.getVisibleLeafColumns()
  const totalSize = table.getTotalSize()

  // Always size columns as a share of the table width so the table fills its
  // container edge-to-edge. In 'fixed' mode a min-width on the table forces
  // horizontal scrolling on narrow screens instead of cramping the columns.
  const widthFor = col => `${(col.getSize() / totalSize) * 100}%`

  const handleDrop = targetId => {
    const src = dragSrc.current
    setDragOver(null)
    dragSrc.current = null
    if (!src || src === targetId) return
    const order = leafColumns.map(c => c.id)
    const from = order.indexOf(src)
    const to = order.indexOf(targetId)
    if (from < 0 || to < 0) return
    order.splice(from, 1)
    order.splice(to, 0, src)
    setColumnOrder(order)
  }

  const tableStyle = {
    width: '100%',
    minWidth: fitMode === 'fixed' ? minWidth || totalSize : undefined,
  }

  return (
    <div className={cn('w-full overflow-x-auto scrollbar-thin', className)}>
      <table className="w-full table-fixed border-collapse text-[13px]" style={tableStyle}>
        <colgroup>
          {leafColumns.map(col => (
            <col key={col.id} style={{ width: widthFor(col) }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="border-b">
              {headerGroup.headers.map(header => {
                const col = header.column
                const canSort = col.getCanSort()
                const canResize = col.getCanResize()
                const canReorder = reorderable && col.columnDef.meta?.reorderable !== false
                const isResizing = !!table.getState().columnSizingInfo.isResizingColumn
                const align = col.columnDef.meta?.align || 'left'
                const sortDir = col.getIsSorted()
                const label =
                  typeof col.columnDef.header === 'string' ? col.columnDef.header : null
                return (
                  <th
                    key={header.id}
                    title={col.columnDef.meta?.headerTitle || label || undefined}
                    draggable={canReorder && !isResizing}
                    onDragStart={e => {
                      // A resize is set synchronously on the grip's mousedown,
                      // before this native dragstart fires on pointer move. Read
                      // it live so we are immune to React re-render timing. Also
                      // bail if the press started on the sort button.
                      if (
                        !canReorder ||
                        suppressReorderRef.current ||
                        table.getState().columnSizingInfo.isResizingColumn
                      ) {
                        e.preventDefault()
                        return
                      }
                      dragSrc.current = col.id
                    }}
                    onDragOver={e => {
                      if (!canReorder || isResizing) return
                      e.preventDefault()
                      setDragOver(col.id)
                    }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={() => { if (canReorder && !isResizing) handleDrop(col.id) }}
                    onDragEnd={() => { setDragOver(null); dragSrc.current = null }}
                    className={cn(
                      'group relative h-8 select-none px-2 align-middle text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
                      align === 'right' && 'text-right',
                      align === 'center' && 'text-center',
                      canReorder && 'cursor-grab active:cursor-grabbing',
                      dragOver === col.id && 'bg-accent',
                      col.columnDef.meta?.headerClassName
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center gap-1',
                        align === 'right' && 'justify-end',
                        align === 'center' && 'justify-center'
                      )}
                    >
                      {header.isPlaceholder ? null : label === null ? (
                        flexRender(col.columnDef.header, header.getContext())
                      ) : canSort ? (
                        <button
                          type="button"
                          onPointerDown={() => { suppressReorderRef.current = true }}
                          onClick={col.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          <span>{label}</span>
                          {sortDir === 'asc' ? (
                            <ArrowUp className="h-3 w-3 text-primary" />
                          ) : sortDir === 'desc' ? (
                            <ArrowDown className="h-3 w-3 text-primary" />
                          ) : (
                            <ChevronsUpDown className="h-3 w-3 opacity-40 group-hover:opacity-70" />
                          )}
                        </button>
                      ) : (
                        <span>{label}</span>
                      )}
                    </div>
                    {canResize && (
                      <span
                        onMouseDown={e => {
                          e.stopPropagation()
                          header.getResizeHandler()(e)
                        }}
                        onTouchStart={e => {
                          e.stopPropagation()
                          header.getResizeHandler()(e)
                        }}
                        onClick={e => e.stopPropagation()}
                        onDragStart={e => e.preventDefault()}
                        draggable={false}
                        className={cn(
                          'absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none select-none',
                          'after:absolute after:right-0 after:top-1 after:h-[calc(100%-0.5rem)] after:w-px after:bg-border after:content-[""]',
                          'hover:after:w-0.5 hover:after:bg-primary',
                          col.getIsResizing() && 'after:w-0.5 after:bg-primary'
                        )}
                      />
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading && data.length === 0 ? (
            <tr>
              <td colSpan={leafColumns.length} className="py-10 text-center">
                <Spinner className="mx-auto" size={24} />
              </td>
            </tr>
          ) : table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={leafColumns.length}
                className="py-10 text-center text-sm text-muted-foreground"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id} className="border-b transition-colors hover:bg-accent/50">
                {row.getVisibleCells().map(cell => {
                  const align = cell.column.columnDef.meta?.align || 'left'
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        'overflow-hidden text-ellipsis whitespace-nowrap px-2 py-1 align-middle',
                        align === 'right' && 'text-right',
                        align === 'center' && 'text-center',
                        cell.column.columnDef.meta?.cellClassName
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  )
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

function readStore(storageKey, suffix, fallback) {
  if (!storageKey) return fallback
  try {
    const raw = window.localStorage.getItem(`${storageKey}:${suffix}`)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

function writeStore(storageKey, suffix, value) {
  try {
    window.localStorage.setItem(`${storageKey}:${suffix}`, JSON.stringify(value))
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export { DataTable }
